import * as React from 'react'

import qrGenerator from 'qrcode-generator'

import { QRCode } from '~/client/src/shared/components/LazyQRCode'
import { QR_CODE_LOGO_SRC } from '~/client/src/shared/utils/usefulStrings'

/**
 * On-screen footprint of the code. This only sets how many physical pixels the
 * symbol gets, not whether it renders correctly. The longest resource URLs
 * produce a 45-module symbol, so 100 leaves ~4.4 device px per module on a
 * retina screen but only ~2.2 on a 1x monitor — raise this if codes have to
 * scan reliably off non-retina displays.
 */
const QR_DISPLAY_SIZE = 100
/**
 * react-qrcode-logo snaps the module grid to whole CSS pixels: it paints module
 * `col` at `Math.round(col * cellSize)` with a width of
 * `ceil((col + 1) * cellSize) - floor(col * cellSize)`. When `size` is not a
 * multiple of the module count, the painted rect overruns the true cell, and
 * once that overrun reaches the neighbouring module's centre a scanner reads
 * the wrong bit. A flat 100px canvas has a 2.7px cell but paints modules 4px
 * wide, which misreads 283 of 1369 modules — well past what error correction
 * level M can repair. Rendering at an exact multiple of the module count makes
 * cellSize a whole number, so nothing rounds; the compositor then scales the
 * canvas down to QR_DISPLAY_SIZE.
 */
const PIXELS_PER_MODULE = 6

const getModuleCount = (value: string): number => {
  const qr = qrGenerator(0, 'M')
  qr.addData(value)
  qr.make()
  return qr.getModuleCount()
}

interface IProps {
  name: string
  url: string
}

const ResourceQRCodeHeader: React.FC<IProps> = ({ name, url }) => {
  const renderSize = React.useMemo(
    () => getModuleCount(url) * PIXELS_PER_MODULE,
    [url],
  )

  return (
    <div className="tw:flex tw:flex-col tw:items-center tw:pb-4">
      <div
        className="tw:overflow-hidden"
        style={{ width: QR_DISPLAY_SIZE, height: QR_DISPLAY_SIZE }}
      >
        <div
          style={{
            transform: `scale(${QR_DISPLAY_SIZE / renderSize})`,
            transformOrigin: 'top left',
          }}
        >
          <QRCode
            value={url}
            logoImage={QR_CODE_LOGO_SRC}
            size={renderSize}
            quietZone={0}
          />
        </div>
      </div>
      <span className="tw:mt-6 tw:text-lg tw:font-bold tw:text-foreground tw:text-center">
        {name}
      </span>
    </div>
  )
}

export default ResourceQRCodeHeader
